import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  FaDatabase, FaToggleOn, FaToggleOff, FaShieldAlt, FaInfoCircle, FaBell,
  FaDownload, FaEye, FaTrash, FaExclamationTriangle, FaCheckCircle, FaGavel
} from 'react-icons/fa';
import {
  fetchUserDataSettings,
  updateUserDataSettings,
  resetUserDataSettings,
  requestDataExport,
  fetchDataUsage,
  deleteAccountThunk,
} from '../../../redux/settingsPages/userData/userDataActions';
import { clearUserDataError } from '../../../redux/settingsPages/userData/userDataSlice';
import { fetchLegalConsent, updateLegalConsent } from '../../../redux/settingsPages/legalConsent/legalConsentActions';

const UserDataPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    settings,
    loading,
    saving,
    error,
    saveError,
    export: exportState,
    deleteStatus,
    deleteError,
    usage,
    usageLoading,
    usageError,
  } = useSelector((state) => state.userData);
  const {
    consent,
    loading: consentLoading,
    updating: consentUpdating,
    error: consentError,
    updateError: consentUpdateError,
  } = useSelector((state) => state.legalConsent);

  const [localSettings, setLocalSettings] = useState(settings);
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [showUsage, setShowUsage] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const DELETE_CONFIRM_PHRASE = 'DELETE MY ACCOUNT';

  // Fetch settings on mount. fetchUserDataSettings is deliberately left
  // unconditional -- `settings` is pre-populated with local DEFAULTS in
  // initial state (not null), so there's no existing signal in this slice
  // that distinguishes "still on local defaults" from "confirmed from
  // server" the way accessibilitySlice's `loaded` flag does; guarding on
  // `settings` truthiness would skip the fetch forever. fetchLegalConsent
  // does have a reliable signal (`consent` starts `null`), so that one is
  // guarded.
  useEffect(() => {
    dispatch(fetchUserDataSettings());
    if (!consent) dispatch(fetchLegalConsent());
  }, [dispatch, consent]);

  // Fetch usage data lazily, the first time the panel is opened
  useEffect(() => {
    if (showUsage && !usage) {
      dispatch(fetchDataUsage());
    }
  }, [showUsage, usage, dispatch]);

  // Update local state when Redux state changes
  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  // Show success message when export is requested
  useEffect(() => {
    if (exportState.status === 'queued' || exportState.status === 'done') {
      alert(t('settings.userData.exportRequestedAlert'));
    }
  }, [exportState.status, t]);

  // Handle deletion confirmation
  useEffect(() => {
    if (deleteStatus === 'scheduled') {
      alert(t('settings.userData.deletionScheduledAlert'));
      setShowDeleteConfirm(false);
      setDeletePassword('');
    }
  }, [deleteStatus, t]);

  const toggleSetting = (setting) => {
    setLocalSettings(prev => ({ ...prev, [setting]: !prev[setting] }));
  };

  const handleSaveChanges = async () => {
    try {
      await dispatch(updateUserDataSettings(localSettings)).unwrap();
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save preferences:', err);
    }
  };

  const handleResetToDefaults = async () => {
    if (window.confirm(t('settings.userData.resetConfirm'))) {
      try {
        await dispatch(resetUserDataSettings()).unwrap();
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } catch (err) {
        console.error('Failed to reset settings:', err);
      }
    }
  };

  const handleDownloadData = async () => {
    try {
      await dispatch(requestDataExport()).unwrap();
      setShowExportConfirm(false);
    } catch (err) {
      console.error('Failed to request data export:', err);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      alert(t('settings.userData.deletePasswordRequiredAlert'));
      return;
    }
    if (deleteConfirmText !== DELETE_CONFIRM_PHRASE) {
      return;
    }

    try {
      await dispatch(deleteAccountThunk()).unwrap();
    } catch (err) {
      console.error('Failed to request account deletion:', err);
    }
  };

  const handleLegalConsentToggle = (field) => {
    if (!consent) return;
    dispatch(updateLegalConsent({ [field]: !consent[field] }));
  };

  const hasChanges = JSON.stringify(localSettings) !== JSON.stringify(settings);
  const displayError = error || saveError || exportState.error || deleteError;
  const isExporting = exportState.status === 'loading';
  const isDeleting = deleteStatus === 'loading';

  return (
    <div className="flex-1 bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('settings.userData.pageTitle')}</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('settings.userData.pageSubtitle')}
        </p>
      </div>

      {/* Success message */}
      {showSuccess && (
        <div className="max-w-6xl mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-600/30 rounded-lg p-4 flex items-center">
          <FaCheckCircle className="text-green-600 dark:text-green-400 mr-3" />
          <p className="text-green-700 dark:text-green-400">{t('settings.userData.savedSuccess')}</p>
        </div>
      )}

      {/* Error message */}
      {displayError && (
        <div className="max-w-6xl mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-600/30 rounded-lg p-4 flex items-center">
          <FaExclamationTriangle className="text-red-600 dark:text-red-400 mr-3 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-red-700 dark:text-red-400">{displayError}</p>
          </div>
          <button
            onClick={() => dispatch(clearUserDataError())}
            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
          >
            ✕
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="max-w-6xl space-y-6">
          {/* Data Collection Settings */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center mb-6">
              <FaDatabase className="text-blue-500 text-xl mr-3" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('settings.userData.dataCollection.title')}</h2>
            </div>
            <div className="space-y-4">
              {['marketData', 'personalizedRecommendations', 'analyticsTracking'].map(key => {
                const label = t(`settings.userData.dataCollection.options.${key}.label`);
                return (
                <div key={key} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">{label}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t(`settings.userData.dataCollection.options.${key}.desc`)}</p>
                  </div>
                  <button
                    onClick={() => toggleSetting(key)}
                    role="switch"
                    aria-checked={!!localSettings[key]}
                    aria-label={label}
                    className="focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                    disabled={saving}
                  >
                    {localSettings[key]
                      ? <FaToggleOn className="text-blue-500 text-2xl" />
                      : <FaToggleOff className="text-gray-400 text-2xl" />
                    }
                  </button>
                </div>
                );
              })}
            </div>
          </div>

          {/* Privacy Controls */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center mb-6">
              <FaShieldAlt className="text-blue-500 text-xl mr-3" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('settings.userData.privacyControls.title')}</h2>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">{t('settings.userData.privacyControls.thirdPartySharing.label')}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('settings.userData.privacyControls.thirdPartySharing.desc')}
                  </p>
                </div>
                <button
                  onClick={() => toggleSetting('thirdPartySharing')}
                  role="switch"
                  aria-checked={!!localSettings.thirdPartySharing}
                  aria-label={t('settings.userData.privacyControls.thirdPartySharing.label')}
                  className="focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                  disabled={saving}
                >
                  {localSettings.thirdPartySharing
                    ? <FaToggleOn className="text-blue-500 text-2xl" />
                    : <FaToggleOff className="text-gray-400 text-2xl" />
                  }
                </button>
              </div>

              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-orange-600 dark:border-yellow-600/30 rounded-lg">
                <div className="flex items-start">
                  <FaInfoCircle className="text-yellow-600 dark:text-yellow-400 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-yellow-800 dark:text-yellow-400">{t('settings.userData.privacyControls.usageNotice.title')}</h3>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                      {t('settings.userData.privacyControls.usageNotice.body')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notification Settings */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center mb-6">
              <FaBell className="text-blue-500 text-xl mr-3" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('settings.userData.notificationPrefs.title')}</h2>
            </div>
            <div className="space-y-4">
              {['emailNotifications', 'pushNotifications', 'smsNotifications'].map(key => {
                const label = t(`settings.userData.notificationPrefs.options.${key}.label`);
                return (
                <div key={key} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">{label}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t(`settings.userData.notificationPrefs.options.${key}.desc`)}</p>
                  </div>
                  <button
                    onClick={() => toggleSetting(key)}
                    role="switch"
                    aria-checked={!!localSettings[key]}
                    aria-label={label}
                    className="focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                    disabled={saving}
                  >
                    {localSettings[key]
                      ? <FaToggleOn className="text-blue-500 text-2xl" />
                      : <FaToggleOff className="text-gray-400 text-2xl" />
                    }
                  </button>
                </div>
                );
              })}
            </div>
          </div>

          {/* Data Management */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center mb-6">
              <FaDatabase className="text-blue-500 text-xl mr-3" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('settings.userData.dataManagement.title')}</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <button
                onClick={() => setShowExportConfirm(true)}
                disabled={isExporting}
                className="flex items-center justify-center p-4 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 border border-blue-200 dark:border-blue-600/30 rounded-lg transition-colors text-blue-700 dark:text-blue-400 disabled:opacity-50"
              >
                {isExporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    {t('settings.userData.dataManagement.processing')}
                  </>
                ) : (
                  <>
                    <FaDownload className="mr-2" />
                    {t('settings.userData.dataManagement.downloadMyData')}
                  </>
                )}
              </button>
              <button
                onClick={() => setShowUsage((v) => !v)}
                className="flex items-center justify-center p-4 bg-gray-50 hover:bg-gray-100 dark:bg-gray-700/20 dark:hover:bg-gray-700/30 border border-gray-200 dark:border-gray-600/30 rounded-lg transition-colors text-gray-700 dark:text-gray-400"
              >
                {usageLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-2"></div>
                ) : (
                  <FaEye className="mr-2" />
                )}
                {t('settings.userData.dataManagement.viewDataUsage')}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center justify-center p-4 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-600/30 rounded-lg transition-colors text-red-700 dark:text-red-400"
              >
                <FaTrash className="mr-2" />
                {t('settings.userData.dataManagement.deleteAccount')}
              </button>
            </div>

            {showUsage && (
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-600/30 rounded-lg">
                {usageError && <p className="text-sm text-red-600 dark:text-red-400">{usageError}</p>}
                {usage && (
                  <div className="space-y-3 text-sm">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">{t('settings.userData.dataManagement.whatWeTrack')}</h4>
                      <div className="space-y-2">
                        {Object.entries(usage.categories || {}).map(([key, val]) => (
                          <div key={key}>
                            <span className="text-gray-900 dark:text-white font-medium">{key.replace(/_/g, ' ')}</span>
                            {val && typeof val === 'object' ? (
                              <p className="text-gray-600 dark:text-gray-400">
                                {val.purpose}{val.retention ? t('settings.userData.dataManagement.retainedSuffix', { retention: val.retention.toLowerCase() }) : ''}
                              </p>
                            ) : (
                              <p className="text-gray-600 dark:text-gray-400">{typeof val === 'boolean' ? (val ? t('settings.userData.dataManagement.enabled') : t('settings.userData.dataManagement.disabled')) : String(val)}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    {usage.third_party_processors?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white mb-1">{t('settings.userData.dataManagement.thirdPartyProcessors')}</h4>
                        <ul className="list-disc list-inside text-gray-600 dark:text-gray-400">
                          {usage.third_party_processors.map((p, i) => (
                            <li key={i}>{p.name || p.processor || JSON.stringify(p)}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {usage.data_protection_officer && (
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white mb-1">{t('settings.userData.dataManagement.dpo')}</h4>
                        <p className="text-gray-600 dark:text-gray-400">
                          {usage.data_protection_officer.email || usage.data_protection_officer.name || t('settings.userData.dataManagement.seePrivacyPolicy')}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-600/30 rounded-lg">
              <div className="flex items-start">
                <FaExclamationTriangle className="text-red-600 dark:text-red-400 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-red-800 dark:text-red-400">{t('settings.userData.dataManagement.deletionWarningTitle')}</h3>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                    {t('settings.userData.dataManagement.deletionWarningBody')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Legal Consent */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center mb-6">
              <FaGavel className="text-blue-500 text-xl mr-3" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('settings.userData.legalConsent.title')}</h2>
            </div>

            {consentError && (
              <p className="text-sm text-red-600 dark:text-red-400 mb-4">{consentError}</p>
            )}
            {consentUpdateError && (
              <p className="text-sm text-red-600 dark:text-red-400 mb-4">{consentUpdateError}</p>
            )}

            {consentLoading || !consent ? (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <div className="space-y-4">
                {[
                  { key: 'terms_accept', version: consent.terms_version, acceptedAt: consent.terms_accepted_at },
                  { key: 'privacy_accept', version: consent.privacy_version, acceptedAt: consent.privacy_accepted_at },
                  { key: 'disclaimer_accept', version: consent.disclaimer_version, acceptedAt: consent.disclaimer_accepted_at },
                ].map((doc) => {
                  const label = t(`settings.userData.legalConsent.docs.${doc.key}`);
                  return (
                  <div key={doc.key} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">{label} <span className="text-xs text-gray-400 font-normal">v{doc.version}</span></h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {consent[doc.key] === false
                          ? t('settings.userData.legalConsent.notAccepted')
                          : doc.acceptedAt
                          ? t('settings.userData.legalConsent.acceptedOn', { date: new Date(doc.acceptedAt).toLocaleDateString() })
                          : t('settings.userData.legalConsent.accepted')}
                      </p>
                    </div>
                    <button
                      onClick={() => handleLegalConsentToggle(doc.key)}
                      disabled={consentUpdating}
                      role="switch"
                      aria-checked={!!consent[doc.key]}
                      aria-label={label}
                      className="focus:outline-none focus:ring-2 focus:ring-blue-500 rounded disabled:opacity-50"
                    >
                      {consent[doc.key]
                        ? <FaToggleOn className="text-blue-500 text-2xl" />
                        : <FaToggleOff className="text-gray-400 text-2xl" />
                      }
                    </button>
                  </div>
                  );
                })}
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('settings.userData.legalConsent.withdrawWarning')}
                </p>
              </div>
            )}
          </div>

          {/* Save Changes */}
          <div className="flex justify-end space-x-4">
            <button
              onClick={handleResetToDefaults}
              disabled={saving}
              className="px-6 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg transition-colors disabled:opacity-50"
            >
              {t('settings.userData.resetToDefaults')}
            </button>
            <button
              onClick={handleSaveChanges}
              disabled={!hasChanges || saving}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('settings.userData.saving')}
                </>
              ) : (
                <>
                  <FaCheckCircle className="mr-2" />
                  {t('settings.userData.saveChanges')}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Data Export Confirmation Modal -- plain-language explanation + confirm
          step, same gate pattern as the delete modal below */}
      {showExportConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              {t('settings.userData.exportModal.title')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {t('settings.userData.exportModal.body')}
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowExportConfirm(false)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg transition-colors"
              >
                {t('settings.userData.exportModal.cancel')}
              </button>
              <button
                onClick={handleDownloadData}
                disabled={isExporting}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center"
              >
                {isExporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('settings.userData.exportModal.requesting')}
                  </>
                ) : (
                  t('settings.userData.exportModal.requestExport')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full border border-red-300 dark:border-red-500/30">
            <h3 className="text-xl font-bold text-red-700 dark:text-red-400 mb-4">
              {t('settings.userData.deleteModal.title')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {t('settings.userData.deleteModal.body')}
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('settings.userData.deleteModal.typeToConfirmPrefix')}{' '}
                <span className="font-mono bg-gray-100 dark:bg-gray-900 px-2 py-1 rounded text-red-600 dark:text-red-400">{DELETE_CONFIRM_PHRASE}</span>{' '}
                {t('settings.userData.deleteModal.confirmSuffix')}
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={DELETE_CONFIRM_PHRASE}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('settings.userData.deleteModal.passwordLabel')}
              </label>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder={t('settings.userData.deleteModal.passwordPlaceholder')}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletePassword('');
                  setDeleteConfirmText('');
                }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg transition-colors"
              >
                {t('settings.userData.deleteModal.cancel')}
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeleting || deleteConfirmText !== DELETE_CONFIRM_PHRASE || !deletePassword}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('settings.userData.deleteModal.processing')}
                  </>
                ) : (
                  t('settings.userData.deleteModal.deleteAccount')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDataPage;